import React from "react";
import { useBreadcrumbs } from "contexts/BreadcrumbsContext";
import UnbilledDropdowns from "./components/UnbilledDropdowns";
import UnbilledTable, { UnbilledRow } from "./components/UnbilledTable";
import { fetchUnbilledYears, fetchUnbilledData, fetchBranchWiseUnbilled, BranchwiseUnbilledRow, fetchBranchCnDetails, BranchCnDetailsRow, downloadBranchCnDetails, downloadAllUnbilledDetails } from "./api/api";
import { updatePnlDropdowns } from "../pnl/api";
import BranchwiseUnbilledTable from "./components/BranchwiseUnbilledTable";
import BranchCnDetailsTable from "./components/BranchCnDetailsTable";


export interface UnbilledLevel {
    id: number | string;
    name: string;
    visible?: boolean;
    selected?: string[] | null;
    options?: Array<{ ou_code: string; ou_name: string }>;
    locked?: string[] | null;
}

type YearMeta = {
    year: string;
    months: Record<number, number[]>;
};

const SORT_MAP: Record<keyof UnbilledRow, string> = {
    Customer: "customer",
    TotalUnbilled: "total_unbilled",
    InTransit: "in_transit",
    InStock: "in_stock",
    DeliveredLessThanEqual10: "delivered_lte_10",
    DeliveredGreaterThan10: "delivered_gt_10",
    PodReceivedLessThanEqual10: "pod_received_lte_10",
    PodReceivedGreaterThan10: "pod_received_gt_10",
};

const BRANCH_SORT_MAP: Record<keyof BranchwiseUnbilledRow, string> = {
    ou_code: "ou_code",
    Branch: "branch",
    TotalUnbilled: "total_unbilled",
    InTransit: "in_transit",
    InStock: "in_stock",
    DeliveredLessThanEqual10: "delivered_lte_10",
    DeliveredGreaterThan10: "delivered_gt_10",
    PodReceivedLessThanEqual10: "pod_received_lte_10",
    PodReceivedGreaterThan10: "pod_received_gt_10",
};

const getFYIndex = (month: number) => {
    return month >= 4 ? month - 3 : month + 9;
};

const getLatestMonthDayForYear = (meta?: YearMeta) => {
    if (!meta) return null;

    const latestMonth = Object.keys(meta.months)
        .map(Number)
        .sort((a, b) => getFYIndex(b) - getFYIndex(a))[0];

    const latestDay = (meta.months[latestMonth] || [])
        .slice()
        .sort((a, b) => b - a)[0];

    return { month: latestMonth, day: latestDay };
};

const UnbilledPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();

    const hasLoadedOnce = React.useRef(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    const [years, setYears] = React.useState<string[]>([]);
    const [year, setYear] = React.useState<string>("");
    const [yearMeta, setYearMeta] = React.useState<YearMeta[]>([]);
    const [month, setMonth] = React.useState<number>(new Date().getMonth() + 1);
    const [day, setDay] = React.useState<number>(new Date().getDate());
    const [rows, setRows] = React.useState<UnbilledRow[]>([]);
    const [tableLoading, setTableLoading] = React.useState<boolean>(false);
    const [conversionType, setConversionType] = React.useState<'INR' | 'Lakhs' | 'Crores'>('Lakhs');
    const [fromDate, setFromDate] = React.useState<string>("");
    const [toDate, setToDate] = React.useState<string>("");

    const [levels, setLevels] = React.useState<UnbilledLevel[]>([]);

    const [page, setPage] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(20);
    const [totalItems, setTotalItems] = React.useState<number>(0);
    const [sortBy, setSortBy] = React.useState<keyof UnbilledRow>("TotalUnbilled");
    const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
    const [search, setSearch] = React.useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = React.useState<string>("");
    const [selectedCustomer, setSelectedCustomer] = React.useState<string | null>(null);

    const [branchRows, setBranchRows] = React.useState<BranchwiseUnbilledRow[]>([]);
    const [branchLoading, setBranchLoading] = React.useState<boolean>(false);
    const [branchPage, setBranchPage] = React.useState<number>(1);
    const [branchPageSize, setBranchPageSize] = React.useState<number>(20);
    const [branchTotalItems, setBranchTotalItems] = React.useState<number>(0);
    const [branchSearch, setBranchSearch] = React.useState<string>("");
    const [debouncedBranchSearch, setDebouncedBranchSearch] = React.useState<string>("");
    const [branchSortBy, setBranchSortBy] = React.useState<keyof BranchwiseUnbilledRow>("Branch");
    const [branchSortOrder, setBranchSortOrder] = React.useState<"asc" | "desc">("asc");

    const [selectedBranch, setSelectedBranch] = React.useState<string | null>(null);
    const [selectedBranchName, setSelectedBranchName] = React.useState<string | null>(null);

    const [cnRows, setCnRows] = React.useState<BranchCnDetailsRow[]>([]);
    const [cnLoading, setCnLoading] = React.useState<boolean>(false);
    const [cnPage, setCnPage] = React.useState<number>(1);
    const [cnPageSize, setCnPageSize] = React.useState<number>(20);
    const [cnTotalItems, setCnTotalItems] = React.useState<number>(0);
    const [cnSortBy, setCnSortBy] = React.useState<string>("cn_date");
    const [cnSortOrder, setCnSortOrder] = React.useState<"asc" | "desc">("desc");
    const [cnSearch, setCnSearch] = React.useState<string>("");
    const [debouncedCnSearch, setDebouncedCnSearch] = React.useState<string>("");
    const [cnStatus, setCnStatus] = React.useState<string | null>(null);

    const [downloadingAllExcel, setDownloadingAllExcel] = React.useState<boolean>(false);
    const [downloadingExcel, setDownloadingExcel] = React.useState<boolean>(false);

    React.useEffect(() => {
        setBreadcrumbs([
            { label: "Reports", href: "/reports/pnl" },
            { label: "Unbilled" },
        ]);

        (async () => {
            const resp = await fetchUnbilledYears();
            const meta = resp?.years || [];

            setYearMeta(meta);

            if (!meta.length) return;

            const latestYear = meta[0];
            const yearValue = latestYear.year;

            // financial order sort
            const monthsSorted = Object.keys(latestYear.months)
                .map(Number)
                .sort((a, b) => getFYIndex(b) - getFYIndex(a));

            const latestMonth = monthsSorted[0];

            const latestDay = (latestYear.months[latestMonth] || [])
                .slice()
                .sort((a, b) => b - a)[0];

            setYears(meta.map((y) => y.year));
            setYear(yearValue);
            setMonth(latestMonth);
            setDay(latestDay);
        })();
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedBranchSearch(branchSearch);
            setBranchPage(1);
        }, 500);

        return () => clearTimeout(timer);
    }, [branchSearch]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedCnSearch(cnSearch);
            setCnPage(1);
        }, 500);

        return () => clearTimeout(timer);
    }, [cnSearch]);

    const buildFilters = React.useCallback(() => {
        const filters: Record<string, string[]> = {};
        levels.forEach((lvl) => {
            if (lvl.selected && lvl.selected.length) {
                filters[String(lvl.id)] = lvl.selected;
            }
        });
        return filters;
    }, [levels]);

    const fetchDropdowns = React.useCallback(async () => {
        if (!year) return;
        setLoading(true);
        try {
            const resp = await updatePnlDropdowns({ year, selection: buildFilters() });
            setLevels(resp?.levels || []);
        } finally {
            setLoading(false);
        }
    }, [year, buildFilters]);

    const fetchTableData = React.useCallback(async () => {
        if (!year) return;
        setTableLoading(true);
        setError(null);
        try {
            const data = await fetchUnbilledData({
                year,
                month,
                day,
                filters: buildFilters(),
                search: debouncedSearch || undefined,
                from_date: fromDate || undefined,
                to_date: toDate || undefined,
                page,
                per_page: pageSize,
                sort_by: SORT_MAP[sortBy],
                sort_order: sortOrder,
            });

            setRows(data.rows);
            setTotalItems(data.total_items);
        } catch (err: any) {
            setRows([]);
            setTotalItems(0);
            setError(err?.response?.data?.detail || err?.message || "Something went wrong");
        } finally {
            setTableLoading(false);
            hasLoadedOnce.current = true;
        }
    }, [year, month, day, page, pageSize, sortBy, sortOrder, buildFilters, debouncedSearch, fromDate, toDate]);

    const handleSelectChange = React.useCallback(
        async (levelId: number | string, value: string[]) => {
            const desc = (levels || []).filter((l) => Boolean(l?.visible)).sort((a, b) => Number(b.id) - Number(a.id));

            const indexById = Object.fromEntries(desc.map((l, i) => [String(l.id), i]));
            const changedIdx = indexById[String(levelId)];

            const nextLevels = levels.map((lvl) => {
                const idx = indexById[String(lvl.id)];
                if (idx < changedIdx) {
                    return lvl.id === levelId ? { ...lvl, selected: value.length > 0 ? value : null } : lvl;
                }

                if (idx === changedIdx) {
                    return { ...lvl, selected: value.length > 0 ? value : null };
                }
                return { ...lvl, selected: null };
            });

            setLevels(nextLevels);

            setLoading(true);
            try {
                const resp = await updatePnlDropdowns({
                    year,
                    selection: nextLevels.reduce((acc, lvl) => {
                        if (lvl.selected?.length) {
                            acc[String(lvl.id)] = lvl.selected;
                        }
                        return acc;
                    }, {} as Record<string, string[]>),
                });

                setLevels(resp?.levels || []);
            } finally {
                setLoading(false);
            }
            setPage(1);
        },
        [levels, year]
    );

    const months = React.useMemo(() => {
        const meta = yearMeta.find((y) => y.year === year);
        if (!meta) return [];

        return Object.keys(meta.months)
            .map(Number)
            .sort((a, b) => getFYIndex(b) - getFYIndex(a)) // latest first
            .map((m) => ({
                value: m,
                label: new Date(0, m - 1).toLocaleString("en", { month: "long" }),
            }));
    }, [yearMeta, year]);

    const days = React.useMemo(() => {
        const meta = yearMeta.find((y) => y.year === year);
        if (!meta) return [];

        return (meta.months[month] || []).slice().sort((a, b) => b - a);
    }, [yearMeta, year, month]);

    React.useEffect(() => {
        if (!year) return;
        fetchTableData();
    }, [year, month, day, levels, page, pageSize, sortBy, sortOrder, debouncedSearch, fromDate, toDate]);

    React.useEffect(() => {
        if (!selectedCustomer || !year) return;

        const loadBranchData = async () => {
            setBranchLoading(true);
            try {
                const resp = await fetchBranchWiseUnbilled({
                    customer_name: selectedCustomer,
                    year,
                    month,
                    day,
                    filters: buildFilters(),
                    search: debouncedBranchSearch || undefined,
                    from_date: fromDate || undefined,
                    to_date: toDate || undefined,
                    page: branchPage,
                    per_page: branchPageSize,
                    sort_by: BRANCH_SORT_MAP[branchSortBy],
                    sort_order: branchSortOrder,
                });
                setBranchRows(resp.rows);
                setBranchTotalItems(resp.total_items);
            } finally {
                setBranchLoading(false);
            }
        };
        loadBranchData();
    }, [selectedCustomer, year, month, day, branchPage, branchPageSize, branchSortBy, branchSortOrder, debouncedBranchSearch, fromDate, toDate, buildFilters]);

    React.useEffect(() => {
        if (!selectedCustomer || !selectedBranch) return;

        const loadCnData = async () => {
            setCnLoading(true);
            try {
                const resp = await fetchBranchCnDetails({
                    customer_name: selectedCustomer,
                    ou_code: selectedBranch,
                    year,
                    month,
                    day,
                    filters: buildFilters(),
                    search: debouncedCnSearch || undefined,
                    status: cnStatus || undefined,
                    from_date: fromDate || undefined,
                    to_date: toDate || undefined,
                    page: cnPage,
                    per_page: cnPageSize,
                    sort_by: cnSortBy,
                    sort_order: cnSortOrder,
                });
                setCnRows(resp.rows);
                setCnTotalItems(resp.total_items);
            } finally {
                setCnLoading(false);
            }
        };
        loadCnData();
    }, [selectedCustomer, selectedBranch, year, month, day, cnPage, cnPageSize, cnSortBy, cnSortOrder, debouncedCnSearch, cnStatus, fromDate, toDate, buildFilters]);

    const handleDownloadAllExcel = async () => {
        if (!year || !month || !day) return;

        try {
            setDownloadingAllExcel(true);
            const blob = await downloadAllUnbilledDetails({
                year,
                month,
                day,
                filters: buildFilters(),
                search: debouncedSearch || undefined,
                from_date: fromDate || undefined,
                to_date: toDate || undefined,
                sort_by: "cn_date",
                sort_order: "desc",
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Unbilled_All_Details_${year}_${month}_${day}.xlsx`;

            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("All unbilled Excel download failed", err);
        } finally {
            setDownloadingAllExcel(false);
        }
    };

    const handleDownloadCnExcel = async () => {
        if (!selectedCustomer || !selectedBranch || !year || !month || !day) return;

        try {
            setDownloadingExcel(true);
            const blob = await downloadBranchCnDetails({
                customer_name: selectedCustomer,
                ou_code: selectedBranch,
                year,
                month,
                day,
                filters: buildFilters(),
                search: debouncedCnSearch || undefined,
                status: cnStatus || undefined,
                from_date: fromDate || undefined,
                to_date: toDate || undefined,
                sort_by: cnSortBy,
                sort_order: cnSortOrder,
            });

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;

            const safeCustomer = selectedCustomer.replace(/[^\w\-. ]/g, "_");
            const safeBranch = (selectedBranchName || selectedBranch).replace(/[^\w\-. ]/g, "_");

            a.download = `Unbilled_CN_Details_${safeCustomer}_${safeBranch}.xlsx`;

            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("CN Excel download failed", err);
        } finally {
            setDownloadingExcel(false);
        }
    };

    React.useEffect(() => {
        if (!year) return;

        fetchDropdowns();
    }, [year]);

    const handleConversionChange = React.useCallback((type: 'INR' | 'Lakhs' | 'Crores') => {
        setConversionType(type);
    }, []);

    const handleCustomerClick = (customerName: string) => {
        setSelectedCustomer(customerName);
        setBranchPage(1);
        setBranchPageSize(20);
    };

    return (
        <div className="flex flex-col h-full gap-4 space-y-2 px-4 sm:px-6 overflow-hidden">
            {!selectedCustomer && (
                <UnbilledDropdowns
                    year={year}
                    years={years}
                    month={month}
                    months={months}
                    day={day}
                    days={days}
                    levels={levels}
                    loading={loading}
                    search={search}
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromDateChange={(val) => {
                        setPage(1);
                        setFromDate(val);
                    }}
                    onToDateChange={(val) => {
                        setPage(1);
                        setToDate(val);
                    }}
                    conversionType={conversionType}
                    onConversionChange={handleConversionChange}
                    onSearchChange={setSearch}
                    onYearChange={(newYear) => {
                        setYear(newYear);
                        setPage(1);

                        const selectedYearMeta = yearMeta.find((y) => y.year === newYear);
                        const latest = getLatestMonthDayForYear(selectedYearMeta);

                        if (latest) {
                            setMonth(latest.month);
                            setDay(latest.day);
                        }
                    }}
                    onMonthChange={setMonth}
                    onDayChange={setDay}
                    onSelectChange={handleSelectChange}
                    onDownloadExcel={handleDownloadAllExcel}
                    downloadingExcel={downloadingAllExcel}
                />
            )}

            {selectedCustomer && selectedBranch ? (
                <BranchCnDetailsTable
                    data={cnRows}
                    loading={cnLoading}
                    page={cnPage}
                    pageSize={cnPageSize}
                    totalItems={cnTotalItems}
                    sortBy={cnSortBy}
                    sortOrder={cnSortOrder}
                    search={cnSearch}
                    status={cnStatus}
                    onSearchChange={setCnSearch}
                    onStatusChange={(s) => {
                        setCnPage(1);
                        setCnStatus(s);
                    }}
                    onSortChange={(col) => {
                        setCnPage(1);

                        if (cnSortBy === col) {
                            setCnSortOrder(p => p === "asc" ? "desc" : "asc");
                        } else {
                            setCnSortBy(col);
                            setCnSortOrder("asc");
                        }
                    }}
                    onPageChange={setCnPage}
                    onPageSizeChange={(s) => {
                        setCnPage(1);
                        setCnPageSize(s);
                    }}
                    onBack={() => {
                        setSelectedBranch(null);
                        setCnSearch("");
                        setCnStatus("");
                    }}
                    customerName={selectedCustomer}
                    branchName={selectedBranchName}
                    onDownloadExcel={handleDownloadCnExcel}
                    downloadingExcel={downloadingExcel}
                />
            ) : selectedCustomer ? (
                <BranchwiseUnbilledTable
                    data={branchRows}
                    loading={branchLoading}
                    customerName={selectedCustomer}
                    page={branchPage}
                    pageSize={branchPageSize}
                    totalItems={branchTotalItems}
                    sortBy={branchSortBy}
                    sortOrder={branchSortOrder}
                    search={branchSearch}
                    onSearchChange={setBranchSearch}
                    onSortChange={(col) => {
                        setBranchPage(1);
                        if (branchSortBy === col) {
                            setBranchSortOrder(p => p === "asc" ? "desc" : "asc");
                        } else {
                            setBranchSortBy(col);
                            setBranchSortOrder("asc");
                        }
                    }}
                    onPageChange={setBranchPage}
                    onPageSizeChange={(s) => {
                        setBranchPage(1);
                        setBranchPageSize(s);
                    }}
                    onBack={() => {
                        setSelectedCustomer(null);
                        setBranchSearch("");
                        setDebouncedBranchSearch("");
                    }}
                    onBranchClick={(ou, name) => {
                        setSelectedBranch(ou);
                        setSelectedBranchName(name);
                        setCnPage(1);
                    }}
                    conversionType={conversionType}
                    onConversionChange={handleConversionChange}
                />
            ) : error ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600 text-sm">{error}</p>
                </div>
            ) : (
                <UnbilledTable
                    data={rows}
                    loading={tableLoading}
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={(col) => {
                        setPage(1);

                        if (sortBy === col) {
                            setSortOrder((p) => (p === "asc" ? "desc" : "asc"));
                        } else {
                            setSortBy(col);
                            setSortOrder("asc");
                        }
                    }}
                    onPageChange={setPage}
                    onPageSizeChange={(s) => {
                        setPage(1);
                        setPageSize(s);
                    }}
                    conversionType={conversionType}
                    onConversionChange={handleConversionChange}
                    onCustomerClick={handleCustomerClick}
                    hasLoadedOnce={hasLoadedOnce.current}
                />
            )}
        </div>
    );
};

export default UnbilledPage;